/* eslint-disable import/no-anonymous-default-export */
import type { Theme } from '@mui/material';
import {
    Chip,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { formatDate } from '@/utils/date_utils';
import {
    RecordContextProvider,
    SimpleListLoading,
    useListContext,
} from 'react-admin';
import { Link } from 'react-router-dom';

import { QuoteJobFolder } from '@/types';
import { defaultTypeOfWork } from '@/root/defaultConfiguration';

const legacyRowSx = {
    bgcolor: 'warning.50',
    borderLeft: 3,
    borderColor: 'warning.main',
    '&:hover': { bgcolor: 'warning.100' },
} as const;

// Shown under the work type while a contract still needs a signature (nothing once signed).
const contractSigningMessage: Record<string, string | undefined> = {
    not_started: 'Contract signing not started yet',
    awaiting_client: 'Awaiting client signature',
    awaiting_mrt: 'Client signed — awaiting countersignature',
};

export const ProjectListContent = () => {
    const { data: projects, error, isPending } = useListContext<QuoteJobFolder>();
    const isSmall = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('md')
    );

    if (isPending) {
        return <SimpleListLoading hasLeftAvatarOrIcon hasSecondaryText />;
    }
    if (error) {
        return null;
    }

    return (
        <List dense>
            {projects.map(item => (
                <RecordContextProvider key={item.id} value={item}>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            to={`/projects/${item.id}/show`}
                            sx={item.has_legacy ? legacyRowSx : undefined}
                        >
                            <ListItemText
                                primary={
                                    <Typography
                                        component="span"
                                        variant="body1"
                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                                    >
                                        {item.has_legacy && (
                                            <Chip label="Legacy" size="small" color="warning" />
                                        )}
                                        {item.name}
                                    </Typography>
                                }
                                secondaryTypographyProps={{ component: 'div' }}
                                secondary={
                                    <>
                                        {item.type_of_work
                                            ? defaultTypeOfWork[item.type_of_work] ??
                                              item.type_of_work
                                            : null}
                                        {item.contract_signing_status &&
                                            contractSigningMessage[
                                                item.contract_signing_status
                                            ] && (
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                    color="warning.main"
                                                    sx={{ display: 'block' }}
                                                >
                                                    {
                                                        contractSigningMessage[
                                                            item.contract_signing_status
                                                        ]
                                                    }
                                                </Typography>
                                            )}
                                    </>
                                }
                            />
                            <ListItemText
                                primary={item.contractor_name}
                                secondary={
                                    item.property_owner_name &&
                                    item.property_owner_name !== item.contractor_name
                                        ? `Owner: ${item.property_owner_name}`
                                        : undefined
                                }
                                sx={{ textAlign: isSmall ? 'left' : 'right', maxWidth: 200 }}
                            />
                            {!isSmall && item.approved_at && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    title={item.approved_at}
                                    sx={{
                                        minWidth: 120,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end',
                                        lineHeight: 1.25,
                                    }}
                                >
                                    <span>{formatDate(item.approved_at, 'MM/dd/yyyy')}</span>
                                    <span>{formatDate(item.approved_at, 'HH:mm')}</span>
                                </Typography>
                            )}
                        </ListItemButton>
                    </ListItem>
                </RecordContextProvider>
            ))}

            {projects.length === 0 && (
                <ListItem>
                    <ListItemText primary="No projects found" />
                </ListItem>
            )}
        </List>
    );
};
